// tslint:disable: max-line-length
import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { Select, Store } from '@ngxs/store';
import { User } from 'app/classes/user';
import { AlertDialogComponent } from 'app/core/components/alert-dialog/alert-dialog.component';
import { ConfirmationDialogComponent } from 'app/core/components/confirmation-dialog/confirmation-dialog.component';
import { UserSearchComponent, UserSearchComponentData } from 'app/core/components/user-search/user-search.component';
import { Group } from 'app/group-management/models/group.model';
import {
  AddMembers,
  AddOwners,
  CreateGroup,
  EditGroup,
  GetGroups,
  RemoveMember,
  RemoveOwner,
  SaveGroup,
  SelectGroup,
} from 'app/group-management/state/group-management.actions';
import { GroupManagementSelectors } from 'app/group-management/state/group-management.selectors';
import * as GuidedTourActions from 'app/guided-tour/state/guided-tour.actions';
import { GuidedTourSelectors } from 'app/guided-tour/state/guided-tour.selectors';
import { AuthService } from 'app/services/auth.service';
import { GuidedTourDataService } from 'app/services/guided-tour-data.service';
import { OfflineService } from 'app/services/offline.service';
import { cloneDeep, isArray, isNil } from 'lodash';
import { Observable, Subscription } from 'rxjs';
import { debounceTime, first } from 'rxjs/operators';
import { CreateGroupDialogComponent } from '../create-group-dialog/create-group-dialog.component';
import { SavingGroupComponent } from '../saving-group/saving-group.component';
// tslint:enable: max-line-length

const debounceTimeMillis = 2000;

export function uniqueName(value: string, names: string[]) {
  if (!value) {
    return null;
  }

  const exists = names.some(name => name.toLowerCase() === value.toLowerCase());
  return exists ? { existingName: { value: value } } : null;
}

/**
 * Simple regex to check email format.
 *
 * @param  {string} email
 * @returns boolean
 */
function validateEmail(email: string): boolean {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(String(email).toLowerCase());
}

@Component({
  selector: 'app-management',
  templateUrl: './management.component.html',
  styleUrls: ['./management.component.scss'],
})
export class ManagementComponent implements OnInit, AfterViewInit, OnDestroy {
  private _groups: Group[] = [];
  private _saving = false;
  private savingNotification: MatSnackBarRef<SavingGroupComponent>;
  private removingUsers = {};
  private errorMessage = '';

  private stateSubs: Subscription[] = [];
  private offlineStatusSubscription: Subscription;
  private guidedTourStatusSubscription: Subscription = new Subscription();

  loading = false;
  editing = false;
  currentUser: User;
  selected: Group;

  guidedTourToggledOn = false;

  @Select(GuidedTourSelectors.toggleStatus)guidedTourToggleStatus$: Observable<boolean>;

  @Select(GroupManagementSelectors.groups) groups$: Observable<Group[]>;
  @Select(GroupManagementSelectors.initializing) initializing$: Observable<boolean>;
  @Select(GroupManagementSelectors.selected) selected$: Observable<Group>;
  @Select(GroupManagementSelectors.saving) saving$: Observable<{ saving: boolean; errorMessage: string }>;
  @Select(GroupManagementSelectors.editing) editing$: Observable<boolean>;
  @Select(GroupManagementSelectors.removingUsers) removingUsers$: Observable<{}>;

  search: FormControl;
  groupName: FormControl;
  isPrivateGroup: FormControl;

  get groups() {
    if (this._groups) {
      return this._groups.sort((a, b) => (a.name.toLowerCase() <= b.name.toLowerCase() ? -1 : 1));
    }

    return [];
  }

  set groups(groups: Group[]) {
    this._groups = groups;
  }

  get saving() {
    if (!this._saving) {
      return false;
    }

    return this._saving;
  }

  set saving(value: boolean) {
    if (value !== this._saving) {
      if (value === true) {
        this.savingNotification = this.snackBar.openFromComponent(SavingGroupComponent);
      } else {
        if (this.savingNotification) {
          this.savingNotification.dismiss();
          this.savingNotification = undefined;
        }

        if (!this.errorMessage) {
          this.snackBar.open('Group Saved', 'Dismiss', { duration: 5000 });
        }
      }

      this._saving = value;
    }
  }

  constructor(
    private formBuilder: FormBuilder,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private store: Store,
    private authService: AuthService,
    private router: Router,
    private offline: OfflineService,
    private guidedTourDataService: GuidedTourDataService
  ) {}

  ngOnInit() {
    this.store.dispatch(GetGroups);

    this.stateSubs.push(
      this.initializing$.subscribe(value => {
        this.loading = value;
      }),
      this.authService.currentUserObs.pipe(first(value => !isNil(value))).subscribe(value => {
        this.currentUser = value;
      }),
      this.groups$.subscribe(value => {
        this.groups = cloneDeep(value);
      }),
      this.selected$.subscribe(value => {
        this.selected = value;
        if (this.groupName) {
          this.groupName.reset(value ? value.name : undefined, { emitEvent: false });
        }
        if (this.isPrivateGroup) {
          this.isPrivateGroup.reset(value ? value.privateGroup : true, { emitEvent: false });
        }
      }),
      this.editing$.subscribe(value => {
        this.editing = value;
      }),
      this.saving$.subscribe(value => {
        this.errorMessage = value.errorMessage;
        this.saving = value.saving;
      }),
      this.removingUsers$.subscribe(value => {
        this.removingUsers = value;
      }),
    );

    this.search = this.formBuilder.control(undefined);
    this.groupName = this.formBuilder.control(undefined, [
      Validators.required,
      (control: AbstractControl) =>
        uniqueName(
          control.value ? control.value.toString().trim() : '',
          this.groups
            .filter(group => this.selected && this.selected.uuid && this.selected.uuid !== group.uuid)
            .map(group => group.name.toLowerCase()),
        ),
    ]);
    this.groupName.valueChanges.pipe(debounceTime(debounceTimeMillis)).subscribe(() => {
      this.saveGroup(this.selected);
    });

    this.isPrivateGroup = this.formBuilder.control(true, [Validators.required]);
    this.isPrivateGroup.valueChanges.subscribe(() => {
      this.saveGroup(this.selected);
    });

    this.offlineStatusSubscription = this.offline.status.subscribe(value => {
      const isOnline = value;
      if (!isOnline) {
        this.snackBar.open('Group Management is not available in offline mode.', 'Dismiss');
        this.router.navigate(['dashboard']);
      }
    });

  }

  ngAfterViewInit() {
    this.store.dispatch(new GuidedTourActions.GetGuidedTourToggleStatus());
    this.guidedTourStatusSubscription = this.guidedTourToggleStatus$.subscribe(status => {
      if (status !== undefined) {
        this.guidedTourToggledOn = status;
        if (this.guidedTourToggledOn) {
          setTimeout(() => this.guidedTourDataService.startGuidedTour('group-management'), 1000);
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.offlineStatusSubscription.unsubscribe();
    this.guidedTourStatusSubscription.unsubscribe();
    this.stateSubs.forEach(sub => sub.unsubscribe());
  }

  select(group: Group) {
    if (
      (this.groupName.touched && this.groupName.dirty && this.groupName.valid) ||
      (this.isPrivateGroup.touched && this.isPrivateGroup.dirty && this.isPrivateGroup.valid)
    ) {
      this.saveGroup(cloneDeep<Group>(this.selected));
      this.switchSelection(group);
    } else if (
      (this.groupName.touched && this.groupName.dirty && !this.groupName.valid) ||
      (this.isPrivateGroup.touched && this.isPrivateGroup.dirty && !this.isPrivateGroup.valid)
    ) {
      this.dialog
        .open(ConfirmationDialogComponent, { data: 'You have invalid group changes. Do you want to correct and save any changes?' })
        .afterClosed()
        .subscribe(result => {
          if (!result) {
            this.switchSelection(group);
          }
        });
    } else {
      this.switchSelection(group);
    }
  }

  private switchSelection(group: Group) {
    this.store.dispatch(new SelectGroup(group));
  }

  createNewGroup() {
    this.dialog
      .open(CreateGroupDialogComponent, { data: { names: this.groups.map(group => group.name.toLowerCase()) } })
      .beforeClosed()
      .subscribe(result => {
        if (result) {
          const group = new Group();
          group.name = result;
          group.owners = [];
          group.members = [];
          group.privateGroup = true;
          group.addOwners([this.currentUser]);

          this.store.dispatch(new CreateGroup(group));
        }
      });
  }

  editGroup() {
    if (this.isOwner(this.selected)) {
      this.store.dispatch(new EditGroup(true));
    }
  }

  cancelEdit() {
    this.store.dispatch(new EditGroup(false));
  }

  saveGroup(group: Group) {
    if ((this.groupName.dirty && this.groupName.valid) || (this.isPrivateGroup.dirty && this.isPrivateGroup.valid)) {
      group.setName(this.groupName.value).setPrivacy(this.isPrivateGroup.value);

      this.store.dispatch(new SaveGroup(group)).subscribe(() => {
        this.groupName.markAsPristine();
        this.isPrivateGroup.markAsPristine();
      });
    }
  }

  removeMember(user: User, request: boolean) {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: request ? 'Are you sure you want to deny this Marine from the group?' : 'Are you sure want to remove this Marine?',
      })
      .afterClosed()
      .subscribe(confirm => {
        if (confirm) {
          this.store.dispatch(new RemoveMember(this.selected, user));
        }
      });
  }

  removeOwner(user: User) {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: 'Are you sure you want to remove this owner?',
      })
      .afterClosed()
      .subscribe(confirm => {
        if (confirm) {
          this.store.dispatch(new RemoveOwner(this.selected, user));
        }
      });
  }

  isRemoving(userUUID: string) {
    return this.removingUsers[userUUID] && this.removingUsers[userUUID] === true;
  }

  isOwner(group: Group) {
    return group.owners.some(user => user.uuid === this.currentUser.uuid);
  }

  onlyOwner(group: Group, user: User) {
    return group.owners.length === 1 && group.owners[0] === user;
  }

  addOwners() {
    this.dialog
      .open(UserSearchComponent, {
        width: '700px',
        data: <UserSearchComponentData>{
          selectedUsers: this.selected.owners,
          disableSelectedUsers: true,
          submitButtonText: 'Make Owner',
          searchByEmail: false,
          excludeBasicUsers: true,
        },
      })
      .beforeClosed()
      .subscribe(result => {
        if (result && result.users && result.users.length > 0) {
          const selectedUsers = result.users.filter(user => this.selected.owners.findIndex(owner => owner.uuid === user.uuid) === -1);

          if (selectedUsers.length > 0) {
            this.saving = true;
            this.store.dispatch(new AddOwners(this.selected, selectedUsers));
          }
        }
      });
  }

  addMarines() {
    this.dialog
      .open(UserSearchComponent, {
        width: '700px',
        data: <UserSearchComponentData>{
          selectedUsers: this.selected.members,
          disableSelectedUsers: true,
          submitButtonText: 'Invite',
          searchByEmail: true,
        },
      })
      .beforeClosed()
      .subscribe(result => {
        if (result) {
          let validEmails: string[] = [];
          if (result.users && isArray(result.users)) {
            validEmails = validEmails.concat(
              this.validateEmails(
                result.users
                  .filter(user => this.selected.members.findIndex(member => member.uuid === user.uuid) === -1)
                  .map(user => user.email),
              ),
            );
          }

          if (result.emails && isArray<string>(result.emails)) {
            validEmails = validEmails.concat(this.validateEmails(result.emails));
          }

          if (validEmails.length > 0) {
            this.saving = true;
            this.store.dispatch(new AddMembers(this.selected, validEmails));
          }
        }
      });
  }

  private validateEmails(emails: any[]): string[] {
    const invalidEmails = [];

    emails.forEach(email => {
      email = email.trim();
      if (!validateEmail(email)) {
        // keep track of emails that are in invalid format
        invalidEmails.push(email);
      }
    });

    // display modal of invalid email addresses
    if (invalidEmails.length > 0) {
      this.dialog.open(AlertDialogComponent, {
        data: {
          content: 'These emails are invalid:\n' + invalidEmails.join('\n'),
        },
      });

      return [];
    }

    return emails;
  }
}
